import {Component, EventEmitter, Input, OnChanges, Output, SimpleChanges} from '@angular/core';
import {EditorSection} from "@app/netscope/netscope-apps-groups-viewer/apps-group-editor/apps-group-editor.component";

export class NewAppsGroupRequest {
  name: string;
  file: string;
  // Section of the editor the group opens on
  way: EditorSection;
}

/**
 * Asks for the name of a new apps group, the file it goes in, and the way its members are recognised first,
 * which only decides where the editor opens: the three ways can be mixed later.
 */
@Component({
  standalone: false,
  selector: 'app-new-apps-group-dialog',
  templateUrl: './new-apps-group-dialog.component.html',
  styleUrls: ['./new-apps-group-dialog.component.css']
})
export class NewAppsGroupDialogComponent implements OnChanges {

  @Input() open = false;
  @Output() openChange: EventEmitter<boolean> = new EventEmitter();
  // Files of the user, the generated ones are read-only
  @Input() files: string[] = [];
  @Input() defaultFile: string;
  // Tells why a name cannot be used in a file, or undefined when it can
  @Input() nameError: (name: string, file: string) => string = () => undefined;

  @Output() create: EventEmitter<NewAppsGroupRequest> = new EventEmitter();

  name = "";
  file: string;
  way: EditorSection = "rules";
  submitted = false;

  ways: { key: EditorSection, icon: string, title: string, text: string, example: string, exampleNote: string, hint: string, cta: string }[] = [
    {
      key: "picked", icon: "check", title: "Specific resources",
      text: "A fixed list you pick one by one: VMs, hosts, networks, IP addresses.",
      example: "db-prod-01, db-prod-02", exampleNote: "Best for a few servers that rarely change.",
      hint: "Next you search the inventory and add resources. The group changes only when you edit it.",
      cta: "Create and pick resources",
    },
    {
      key: "rules", icon: "filter", title: "By name",
      text: "Everything named like a pattern. New VMs named that way join by themselves.",
      example: "web-prod-*  except *-old-*", exampleNote: "Follows the inventory as it changes.",
      hint: "Next you type a pattern and see right away which VMs and hosts it catches, before saving.",
      cta: "Create and write a rule",
    },
    {
      key: "groups", icon: "layers", title: "From other groups",
      text: "A larger group made of smaller ones, for a whole application or a shared service.",
      example: "web_front + api + databases", exampleNote: "Follows the groups it includes.",
      hint: "Next you choose the groups to include, from this file or another. They stay linked.",
      cta: "Create and include groups",
    },
  ];

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['open'] && this.open) {
      this.name = "";
      this.file = this.files.includes(this.defaultFile) ? this.defaultFile : this.files[0];
      this.way = "rules";
      this.submitted = false;
    }
  }

  get currentWay() {
    return this.ways.find((way) => way.key == this.way);
  }

  get error(): string {
    if (this.file == undefined) {
      return "Create a file of your own first: the generated ones are read-only.";
    }
    return this.nameError(this.name.trim(), this.file);
  }

  close = () => {
    this.open = false;
    this.openChange.emit(false);
  }

  submit = () => {
    this.submitted = true;
    if (this.error != undefined) {
      return;
    }
    this.create.emit({name: this.name.trim(), file: this.file, way: this.way});
    this.close();
  }
}
